/**
 * iter211f — AddRectificationPrompt
 *
 * Amber prompt that renders on any diagnostic job group that hasn't yet
 * had a rectification (parts + labour) linked to it. FOH clicks "Add
 * rectification", picks from the 6-code catalogue (or free-text
 * fallback), and the backend creates a new job group linked back to
 * the diag. Pricing / CAE / portal all pick it up automatically.
 *
 * Rendered inside the FOH pricing card for diagnostic JGs.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { AlertTriangle, Wrench, Plus, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AddRectificationPrompt = ({
  yestimateId,
  diagJobGroupId,
  diagConclusionText,
  diagJobGroup,      // full JG so we can read `rectification_deferred`
  // vehicleInfo prop reserved for future use (custom variant labels)
  onRectificationAdded,
  onRectificationDeferred,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState("picker"); // 'picker' | 'freetext'
  const [catalogue, setCatalogue] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [freetext, setFreetext] = useState({
    title: "",
    parts_description: "",
    parts_price_inc_vat: "",
    labour_hours: "",
    customer_note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!expanded || catalogue.length > 0) return;
    (async () => {
      try {
        // Pass yestimate_id so backend can return vehicle-aware
        // variant recommendations (e.g. AGM for Jaguar I-Pace).
        const url = yestimateId
          ? `${API}/rectification-catalogue?yestimate_id=${encodeURIComponent(yestimateId)}`
          : `${API}/rectification-catalogue`;
        const r = await axios.get(url);
        setCatalogue(r.data.catalogue || []);
      } catch (err) {
        toast.error("Could not load rectification catalogue");
      }
    })();
  }, [expanded, catalogue.length, yestimateId]);

  const activeEntry = catalogue.find((c) => c.code === selectedCode);

  // iter211f2 — When the picked code has a vehicle-aware recommendation,
  // pre-select the recommended variant. FOH can still override.
  useEffect(() => {
    if (!activeEntry) return;
    if (activeEntry.recommended_variant && !selectedVariant) {
      setSelectedVariant(activeEntry.recommended_variant);
    }
  }, [activeEntry, selectedVariant]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { source_diag_jg_id: diagJobGroupId };
      if (mode === "picker") {
        if (!selectedCode) {
          toast.error("Pick a rectification from the list");
          setSubmitting(false);
          return;
        }
        payload.code = selectedCode;
        if (selectedVariant) payload.variant_code = selectedVariant;
      } else {
        if (!freetext.title || (!freetext.labour_hours && !freetext.parts_price_inc_vat)) {
          toast.error("Title + at least labour hours OR parts price are required");
          setSubmitting(false);
          return;
        }
        Object.assign(payload, {
          title: freetext.title,
          parts_description: freetext.parts_description,
          parts_price_inc_vat: parseFloat(freetext.parts_price_inc_vat) || 0,
          labour_hours: parseFloat(freetext.labour_hours) || 0,
          customer_note: freetext.customer_note,
        });
      }
      const r = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/add-rectification`,
        payload
      );
      toast.success("Rectification added — pricing updated");
      setExpanded(false);
      setSelectedCode("");
      setSelectedVariant("");
      if (onRectificationAdded) onRectificationAdded(r.data.job_group);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed to add rectification";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDefer = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/diag-jg/${diagJobGroupId}/defer-rectification`,
        { reason: "Strip & investigate — rectification TBD" }
      );
      toast.success("Rectification deferred — pricing can now be marked complete");
      if (onRectificationDeferred) onRectificationDeferred();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not defer rectification";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndoDefer = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/diag-jg/${diagJobGroupId}/defer-rectification`,
        { undo: true }
      );
      toast.success("Rectification prompt re-enabled");
      if (onRectificationDeferred) onRectificationDeferred();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not re-enable prompt";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // iter211h — Deferred state: FOH marked this diag as "strip &
  // investigate" so no rectification is expected until the tech
  // opens the assembly. Show a small non-blocking confirmation
  // strip with an undo option; suppress the amber prompt entirely.
  if (diagJobGroup?.rectification_deferred) {
    return (
      <div
        className="mt-2 p-2.5 border border-slate-200 bg-slate-50 rounded-lg flex items-center gap-2 text-sm"
        data-testid={`rectification-deferred-strip-${diagJobGroupId}`}
      >
        <Check className="w-4 h-4 text-slate-500 shrink-0" />
        <span className="flex-1 min-w-0 text-slate-700">
          Rectification deferred — will be quoted once the tech has opened it up.
        </span>
        <button
          onClick={handleUndoDefer}
          disabled={submitting}
          className="text-xs text-slate-500 hover:text-slate-800 underline shrink-0"
          data-testid={`rectification-undo-defer-${diagJobGroupId}`}
        >
          Undo
        </button>
      </div>
    );
  }

  if (!expanded) {
    return (
      <div
        className="mt-2 p-3 border border-amber-300 bg-amber-50 rounded-lg flex items-start gap-2"
        data-testid={`add-rectification-prompt-${diagJobGroupId}`}
      >
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-900">
            Rectification needed
          </p>
          <p className="text-xs text-amber-700 mt-0.5 line-clamp-2">
            {diagConclusionText
              ? `Tech concluded: "${diagConclusionText}"`
              : "This diagnostic has no rectification job priced yet."}
          </p>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button
            size="sm"
            onClick={() => setExpanded(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid={`add-rectification-open-btn-${diagJobGroupId}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add rectification
          </Button>
          <button
            onClick={handleDefer}
            disabled={submitting}
            className="text-[10px] text-amber-700 hover:text-amber-900 underline text-center"
            data-testid={`add-rectification-defer-btn-${diagJobGroupId}`}
          >
            Not needed at this stage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-2 p-3 border-2 border-amber-400 bg-amber-50 rounded-lg space-y-3"
      data-testid={`add-rectification-panel-${diagJobGroupId}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-amber-900">Add rectification</span>
        </div>
        <button
          onClick={() => setExpanded(false)}
          className="text-amber-700 hover:text-amber-900"
          data-testid={`add-rectification-close-${diagJobGroupId}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 bg-white p-1 rounded-md border border-amber-200">
        <button
          onClick={() => setMode("picker")}
          className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-all ${
            mode === "picker"
              ? "bg-amber-500 text-white"
              : "text-amber-700 hover:bg-amber-100"
          }`}
          data-testid={`add-rectification-mode-picker-${diagJobGroupId}`}
        >
          Catalogue
        </button>
        <button
          onClick={() => setMode("freetext")}
          className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-all ${
            mode === "freetext"
              ? "bg-amber-500 text-white"
              : "text-amber-700 hover:bg-amber-100"
          }`}
          data-testid={`add-rectification-mode-freetext-${diagJobGroupId}`}
        >
          Free text
        </button>
      </div>

      {mode === "picker" ? (
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-amber-900">
            Choose rectification
          </Label>
          <select
            value={selectedCode}
            onChange={(e) => {
              setSelectedCode(e.target.value);
              setSelectedVariant("");
            }}
            className="w-full p-2 border border-amber-300 rounded text-sm bg-white"
            data-testid={`add-rectification-code-select-${diagJobGroupId}`}
          >
            <option value="">— Pick a rectification —</option>
            {catalogue.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
          {activeEntry && activeEntry.variants.length > 0 && (
            <>
              <Label className="text-xs font-semibold text-amber-900 mt-2 block">
                Option
              </Label>
              {activeEntry.recommended_variant && activeEntry.recommended_reason && (
                <div
                  className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-2 py-1.5 flex items-start gap-1.5"
                  data-testid={`add-rectification-recommendation-${diagJobGroupId}`}
                >
                  <span className="font-semibold shrink-0">✓ Recommended:</span>
                  <span>{activeEntry.recommended_reason}</span>
                </div>
              )}
              <div className="grid grid-cols-1 gap-1">
                {activeEntry.variants.map((v) => (
                  <button
                    key={v.code}
                    onClick={() => setSelectedVariant(v.code)}
                    className={`text-left text-xs py-1.5 px-2 rounded border transition-all flex items-center justify-between ${
                      selectedVariant === v.code
                        ? "bg-amber-500 text-white border-amber-600"
                        : "bg-white border-amber-200 hover:border-amber-400"
                    }`}
                    data-testid={`add-rectification-variant-${v.code}-${diagJobGroupId}`}
                  >
                    <span>{v.label}</span>
                    {activeEntry.recommended_variant === v.code && (
                      <span className={`text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded ${
                        selectedVariant === v.code ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        Recommended
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
          {activeEntry && activeEntry.coding_required && (
            <p className="text-xs text-amber-700 italic">
              ⓘ Coding / ECU registration required — labour includes 0.2h coding time.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <div>
            <Label className="text-xs font-semibold text-amber-900">
              Rectification title
            </Label>
            <Input
              value={freetext.title}
              onChange={(e) => setFreetext({ ...freetext, title: e.target.value })}
              placeholder="e.g. Wiring loom repair — tail-light circuit"
              className="text-sm"
              data-testid={`add-rectification-freetext-title-${diagJobGroupId}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold text-amber-900">
                Parts £ (inc VAT)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={freetext.parts_price_inc_vat}
                onChange={(e) => setFreetext({ ...freetext, parts_price_inc_vat: e.target.value })}
                placeholder="0.00"
                className="text-sm"
                data-testid={`add-rectification-freetext-parts-price-${diagJobGroupId}`}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-amber-900">
                Labour hours
              </Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={freetext.labour_hours}
                onChange={(e) => setFreetext({ ...freetext, labour_hours: e.target.value })}
                placeholder="0.0"
                className="text-sm"
                data-testid={`add-rectification-freetext-labour-hours-${diagJobGroupId}`}
              />
            </div>
          </div>
          {(parseFloat(freetext.parts_price_inc_vat) || 0) > 0 && (
            <div>
              <Label className="text-xs font-semibold text-amber-900">
                Parts description
              </Label>
              <Input
                value={freetext.parts_description}
                onChange={(e) => setFreetext({ ...freetext, parts_description: e.target.value })}
                placeholder="e.g. Loom repair kit + heatshrink"
                className="text-sm"
                data-testid={`add-rectification-freetext-parts-desc-${diagJobGroupId}`}
              />
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-amber-900">
              Customer-facing note (optional)
            </Label>
            <Textarea
              value={freetext.customer_note}
              onChange={(e) => setFreetext({ ...freetext, customer_note: e.target.value })}
              placeholder="What this repair does, in plain English."
              className="text-sm min-h-[60px]"
              data-testid={`add-rectification-freetext-note-${diagJobGroupId}`}
            />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
          data-testid={`add-rectification-submit-${diagJobGroupId}`}
        >
          {submitting ? (
            "Adding..."
          ) : (
            <>
              <Check className="w-4 h-4 mr-1" />
              Add to pricing
            </>
          )}
        </Button>
        <Button
          onClick={() => setExpanded(false)}
          variant="outline"
          className="border-amber-300 text-amber-700"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
